import React,{ createContext,useCallback,useContext,useEffect,useMemo,useRef,useState } from "react";
import { loadAuthInfoFromStorage } from "../auth/authStorage";
import { useRealtimeEvent } from "../realtime/RealtimeContext";
import { RealtimeEventTypes } from "../realtime/realtimeEventTypes";
import { openSharedEventStream } from "../../../../../../shared/frontend/live/sharedEventStream";

/** Länge des Trenners zwischen zwei Server-Sent-Events: zwei Zeilenumbrüche. */
const SSE_FRAME_DELIMITER_LENGTH = 2;

/**
 * Globale Verwaltung von Hintergrundjobs (lang laufende Server-Aktionen wie Bulk-Delete,
 * Schema-Update, Commit). Jede Komponente kann einen Job registrieren und live Updates
 * erhalten. Listen-Komponenten koennen Jobs per `relatedEntityId` direkt in der passenden
 * Zeile, einem Overlay oder einem Container anzeigen, sodass der User sie auch nach
 * Seitenwechsel wiederfindet.
 *
 * Live-Updates kommen ueber den zentralen Realtime-Stream (`jobs.progress`). Container-Restart-
 * Erkennung laeuft ueber den zentralen Sweep `GET /api/v1/jobs` (alle 3s, ein Request fuer alle
 * offenen Jobs eines Tenants — listet sowohl In-Memory- als auch persistierte PlatformJobs).
 */

export type JobStatus = "Queued" | "Running" | "Succeeded" | "Failed" | "Cancelled";

export interface JobInfo {
  id: string;
  jobType: string;
  title: string;
  status: JobStatus;
  current: number;
  total: number;
  message: string | null;
  error: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  /** Manche Jobs duerfen fachlich nicht abgebrochen werden, z. B. Finanzbuchungen. */
  cancellable?: boolean;
  /** Mandanten-ID fuer Snapshot-Requests und Realtime-Events. */
  tenantId?: string | null;
  /**
   * Optional: Id der fachlichen Entitaet, die zu diesem Job gehoert (z. B. ImportRun.Id). Listen-Komponenten
   * koennen damit den Job ihrer Zeile zuordnen und den Fortschritt inline anzeigen.
   */
  relatedEntityId?: string | null;
  resetProgress?: boolean;
}

interface JobTrackerState {
  jobs: Record<string, JobInfo>;
}

interface JobTrackerContextValue {
  jobs: Record<string, JobInfo>;
  /** Registriert einen neu gestarteten Job. Live-Updates kommen ueber den zentralen Realtime-Stream. */
  registerJob: (job: JobInfo) => void;
  /** Job aus dem Tracker entfernen (z. B. nach User-Bestaetigung des Abschlusses). */
  dismissJob: (jobId: string) => void;
  cancelJob: (jobId: string) => void;
}

const JobTrackerContext = createContext<JobTrackerContextValue | null>(null);

function getStoredTenantId(): string {
  try {
    return String(loadAuthInfoFromStorage()?.tenantId ?? "").trim();
  } catch {
    return "";
  }
}

export function useJobTracker(): JobTrackerContextValue {
  const ctx = useContext(JobTrackerContext);
  if (!ctx) throw new Error("useJobTracker: kein JobTrackerProvider im Tree");
  return ctx;
}

function isFinalJobStatus(status: JobStatus): boolean {
  return status === "Succeeded" || status === "Failed" || status === "Cancelled";
}

function getProgressRatio(current: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.max(0, Math.min(1, current / total));
}

export function JobTrackerProvider({ children, authTokenGetter }: {
  children: React.ReactNode;
  /** Liefert einen Bearer-Token fuer API-Calls. */
  authTokenGetter: () => string;
}) {
  const [state, setState] = useState<JobTrackerState>({ jobs: {} });
  const jobsRef = useRef<Record<string, JobInfo>>({});
  /** Zaehler je Job, wie oft er im zentralen Sweep gefehlt hat — fuer Container-Restart-Erkennung. */
  const missingCountsRef = useRef<Map<string, number>>(new Map());
  /** Offene Einzeljob-Streams fuer Jobs fremder Mandanten (Plattform-Admin-Ansichten). */
  const crossTenantStreamsRef = useRef<Map<string, AbortController>>(new Map());
  const tokenGetterRef = useRef(authTokenGetter);
  tokenGetterRef.current = authTokenGetter;

  const upsertJob = useCallback((update: Partial<JobInfo> & { id: string }) => {
    setState((prev) => {
      const existing = prev.jobs[update.id];
      const nextStatus = (update.status ?? existing?.status ?? "Queued") as JobStatus;
      const nextCurrent = update.current ?? existing?.current ?? 0;
      const nextTotal = update.total ?? existing?.total ?? 0;
      const existingRatio = existing ? getProgressRatio(existing.current, existing.total) : null;
      const nextRatio = getProgressRatio(nextCurrent, nextTotal);
      const resetProgress = update.resetProgress === true;
      const keepExistingProgress = !!existing
        && !resetProgress
        && !isFinalJobStatus(nextStatus)
        && (
          (existing.total > 0 && nextTotal <= 0)
          || (
            existing.total === nextTotal
            && existingRatio !== null
            && nextRatio !== null
            && nextRatio < existingRatio
          )
        );
      const next: JobInfo = {
        id: update.id,
        jobType: update.jobType ?? existing?.jobType ?? "",
        title: update.title ?? existing?.title ?? "",
        status: nextStatus,
        current: keepExistingProgress ? existing.current : nextCurrent,
        total: keepExistingProgress ? existing.total : nextTotal,
        message: update.message !== undefined ? update.message : (existing?.message ?? null),
        error: update.error !== undefined ? update.error : (existing?.error ?? null),
        startedAt: update.startedAt !== undefined ? update.startedAt : existing?.startedAt,
        finishedAt: update.finishedAt !== undefined ? update.finishedAt : existing?.finishedAt,
        cancellable: update.cancellable !== undefined ? update.cancellable : existing?.cancellable,
        tenantId: update.tenantId !== undefined ? update.tenantId : existing?.tenantId,
        relatedEntityId: update.relatedEntityId !== undefined ? update.relatedEntityId : existing?.relatedEntityId
      };
      const jobs = { ...prev.jobs, [update.id]: next };
      jobsRef.current = jobs;
      return { jobs };
    });
  }, []);

  const disconnectStream = useCallback((jobId: string) => {
    missingCountsRef.current.delete(jobId);
    const controller = crossTenantStreamsRef.current.get(jobId);
    if (controller) {
      controller.abort();
      crossTenantStreamsRef.current.delete(jobId);
    }
  }, []);

  /**
   * Einzeljob-Stream fuer Jobs eines fremden Mandanten. Der zentrale Realtime-Stream ist auf genau
   * einen Mandanten abonniert (den der Sitzung) — ein Plattform-Admin, der eine fremde Mandanten-
   * ansicht bedient (z. B. Datensicherung im Admin-Bereich), bekommt dessen `jobs.progress`-Events
   * deshalb nie. Ohne diesen Stream bliebe nur der 3s-Sweep, der bei kurzen Jobs komplett danebengreift
   * und die Zeile die ganze Laufzeit auf 0 % stehen laesst.
   */
  const connectCrossTenantStream = useCallback((jobId: string, jobTenantId: string) => {
    if (!jobId || !jobTenantId) return;
    if (crossTenantStreamsRef.current.has(jobId)) return;
    const token = tokenGetterRef.current();
    if (!token) return;

    const controller = new AbortController();
    crossTenantStreamsRef.current.set(jobId, controller);
    void (async () => {
      try {
        const res = await openSharedEventStream(`/api/v1/jobs/${encodeURIComponent(jobId)}/stream`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": jobTenantId,
            Accept: "text/event-stream"
          },
          signal: controller.signal
        });
        if (!res.ok || !res.body) return;

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        while (!controller.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
          for (;;) {
            const frameEnd = buffer.indexOf("\n\n");
            if (frameEnd < 0) break;
            const frame = buffer.slice(0, frameEnd);
            buffer = buffer.slice(frameEnd + SSE_FRAME_DELIMITER_LENGTH);
            const dataLine = frame.split("\n").find((line) => line.startsWith("data:"));
            if (!dataLine) continue;
            let data: {
              status?: JobStatus;
              current?: number;
              total?: number;
              message?: string | null;
              error?: string | null;
              startedAt?: string | null;
              finishedAt?: string | null;
              relatedEntityId?: string | null;
            };
            try {
              data = JSON.parse(dataLine.slice("data:".length).trim());
            } catch {
              continue;
            }
            const status = (data.status ?? "Running") as JobStatus;
            upsertJob({
              id: jobId,
              status,
              current: Number(data.current ?? 0),
              total: Number(data.total ?? 0),
              message: data.message ?? null,
              error: data.error ?? null,
              startedAt: data.startedAt ?? undefined,
              finishedAt: data.finishedAt ?? undefined,
              relatedEntityId: data.relatedEntityId ?? undefined,
              tenantId: jobTenantId
            });
            if (isFinalJobStatus(status)) return;
          }
        }
      } catch {
        // Best effort: der zentrale Sweep bleibt als Fallback.
      } finally {
        controller.abort();
        crossTenantStreamsRef.current.delete(jobId);
      }
    })();
  }, [upsertJob]);

  /** Jobs des eigenen Mandanten laufen ueber den zentralen Stream; nur fremde brauchen einen eigenen. */
  const ensureLiveUpdates = useCallback((job: { id: string; status: JobStatus; tenantId?: string | null }) => {
    if (isFinalJobStatus(job.status)) return;
    const jobTenantId = (job.tenantId ?? "").trim();
    if (!jobTenantId) return;
    const sessionTenantId = getStoredTenantId();
    if (!sessionTenantId || jobTenantId.toLowerCase() === sessionTenantId.toLowerCase()) return;
    connectCrossTenantStream(job.id, jobTenantId);
  }, [connectCrossTenantStream]);

  const registerJob = useCallback((job: JobInfo) => {
    upsertJob({ ...job });
    ensureLiveUpdates(job);
  }, [ensureLiveUpdates, upsertJob]);

  const dismissJob = useCallback((jobId: string) => {
    disconnectStream(jobId);
    setState((prev) => {
      if (!prev.jobs[jobId]) return prev;
      const { [jobId]: _gone, ...rest } = prev.jobs;
      jobsRef.current = rest;
      return { jobs: rest };
    });
  }, [disconnectStream]);

  const cancelJob = useCallback((jobId: string) => {
    const token = tokenGetterRef.current();
    const tenantId = (jobsRef.current[jobId]?.tenantId ?? "").trim() || getStoredTenantId();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (tenantId) headers["X-Tenant-Id"] = tenantId;
    void fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: "POST",
      headers
    });
  }, []);

  useRealtimeEvent<{
    jobId?: string;
    id?: string;
    jobType?: string;
    title?: string;
    status?: JobStatus;
    current?: number;
    total?: number;
    message?: string | null;
    error?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
    relatedEntityId?: string | null;
    cancellable?: boolean;
    tenantId?: string | null;
  }>(RealtimeEventTypes.JobsProgress, (message) => {
    const data = message.payload ?? {};
    const id = String(data.jobId || data.id || "").trim();
    if (!id) return;
    const status = (data.status ?? "Running") as JobStatus;
    upsertJob({
      id,
      jobType: data.jobType,
      title: data.title,
      status,
      current: Number(data.current ?? 0),
      total: Number(data.total ?? 0),
      message: data.message ?? null,
      error: data.error ?? null,
      startedAt: data.startedAt ?? undefined,
      finishedAt: data.finishedAt ?? undefined,
      relatedEntityId: data.relatedEntityId ?? undefined,
      cancellable: data.cancellable,
      tenantId: data.tenantId ?? message.tenantId ?? undefined
    });
    if (isFinalJobStatus(status)) {
      disconnectStream(id);
    }
  });

  useEffect(() => () => {
    missingCountsRef.current.clear();
    for (const controller of crossTenantStreamsRef.current.values()) controller.abort();
    crossTenantStreamsRef.current.clear();
  }, []);

  /**
   * Zentraler Job-Sweep: ein Request alle 3s fuer alle offenen Jobs des Tenants.
   * - Bindet persistierte Platform-Jobs (z. B. Mandanten-Backup) nach Reload wieder an.
   * - Hydriert Status fuer aktive Jobs als SSE-Watchdog (falls SSE durch nginx-Buffering haengt).
   * - Erkennt Container-Restart: Jobs, die zwei Sweeps in Folge nicht mehr in der Server-Liste
   *   stehen, werden als Cancelled markiert (analog zum frueheren Pro-Job-Polling-404-Pfad).
   */
  useEffect(() => {
    let cancelled = false;
    const MISSING_THRESHOLD = 2;
    const GRACE_MS = 6000;
    const refreshOpenPlatformJobs = async () => {
      const token = tokenGetterRef.current();
      if (!token) return;
      try {
        const tenantId = getStoredTenantId();
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
        if (tenantId) headers["X-Tenant-Id"] = tenantId;
        // Dem Server die noch nicht final getrackten Jobs nennen, damit er deren Endstatus genau
        // einmal mitliefert (SSE-Watchdog) — ohne dass das Backend per Zeitfenster raten muss.
        const trackedIds = Object.values(jobsRef.current)
          .filter((job) => !isFinalJobStatus(job.status))
          .map((job) => job.id);
        const url = trackedIds.length > 0
          ? `/api/v1/jobs?track=${encodeURIComponent(trackedIds.join(","))}`
          : "/api/v1/jobs";
        const r = await fetch(url, { headers });
        if (!r.ok || cancelled) return;
        const list = (await r.json()) as Array<{
          id: string;
          jobType: string;
          title: string;
          status: JobStatus;
          current: number;
          total: number;
          message: string | null;
          error: string | null;
          startedAt?: string | null;
          finishedAt?: string | null;
          cancellable?: boolean;
          tenantId?: string | null;
          relatedEntityId?: string | null;
        }>;
        const serverIds = new Set<string>();
        for (const j of list) {
          serverIds.add(j.id);
          if (isFinalJobStatus(j.status)) {
            // Server hat finalen Status — uebernehmen, aber keinen neuen SSE-Stream aufbauen.
            if (jobsRef.current[j.id]) {
              upsertJob({
                id: j.id,
                status: j.status,
                current: j.current,
                total: j.total,
                message: j.message,
                error: j.error,
                startedAt: j.startedAt ?? undefined,
                finishedAt: j.finishedAt ?? undefined,
                cancellable: j.cancellable,
                relatedEntityId: j.relatedEntityId ?? undefined
              });
              disconnectStream(j.id);
            }
            missingCountsRef.current.delete(j.id);
            continue;
          }
          if (jobsRef.current[j.id]) {
            // Bekannter Job — Status hydratisieren (Watchdog gegen haengende SSE).
            upsertJob({
              id: j.id,
              status: j.status,
              current: j.current,
              total: j.total,
              message: j.message,
              error: j.error,
              startedAt: j.startedAt ?? undefined,
              finishedAt: j.finishedAt ?? undefined,
              cancellable: j.cancellable,
              relatedEntityId: j.relatedEntityId ?? undefined
            });
            ensureLiveUpdates({ id: j.id, status: j.status, tenantId: j.tenantId });
          } else {
            // Neuer Job (z. B. Reload, fremder Tab) — registrieren; Live-Updates kommen zentral.
            registerJob({
              id: j.id,
              jobType: j.jobType,
              title: j.title,
              status: j.status,
              current: j.current,
              total: j.total,
              message: j.message,
              error: j.error,
              startedAt: j.startedAt ?? null,
              finishedAt: j.finishedAt ?? null,
              cancellable: j.cancellable,
              tenantId: j.tenantId ?? null,
              relatedEntityId: j.relatedEntityId ?? null
            });
          }
          missingCountsRef.current.delete(j.id);
        }

        // Container-Restart-Erkennung: lokale Jobs, die der Server nicht mehr listet.
        const now = Date.now();
        for (const localId of Object.keys(jobsRef.current)) {
          const job = jobsRef.current[localId];
          if (!job) continue;
          if (isFinalJobStatus(job.status)) {
            missingCountsRef.current.delete(localId);
            continue;
          }
          if (serverIds.has(localId)) continue;
          // Grace-Period: gerade frisch registrierte Jobs sind noch nicht in der Server-Liste.
          const startedMs = job.startedAt ? Date.parse(job.startedAt) : 0;
          if (startedMs && now - startedMs < GRACE_MS) continue;
          const next = (missingCountsRef.current.get(localId) ?? 0) + 1;
          missingCountsRef.current.set(localId, next);
          if (next >= MISSING_THRESHOLD) {
            upsertJob({
              id: localId,
              status: "Cancelled",
              message: null,
              error: "Job ist nicht mehr im Server-Cache (Backend-Restart?)",
              finishedAt: new Date().toISOString()
            });
            disconnectStream(localId);
          }
        }
      } catch {
        // best effort
      }
    };

    // Adaptive Frequenz: Live-Updates kommen primaer ueber den Realtime-Stream (jobs.progress).
    // Dieser Sweep ist nur Watchdog (haengende SSE) + Restart-Erkennung + Rehydrate nach Reload.
    // Solange Jobs laufen eng takten (3s); im Leerlauf reicht ein seltener Sweep, um z. B. in
    // anderen Tabs gestartete Jobs aufzufangen — das spart die Dauer-Last des 3s-Polls.
    const ACTIVE_INTERVAL_MS = 3000;
    const IDLE_INTERVAL_MS = 15000;
    let timeoutId = 0;
    const hasActiveJobs = () =>
      Object.values(jobsRef.current).some((job) => !isFinalJobStatus(job.status));
    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = window.setTimeout(async () => {
        await refreshOpenPlatformJobs();
        scheduleNext();
      }, hasActiveJobs() ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS);
    };
    void refreshOpenPlatformJobs();
    scheduleNext();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refreshOpenPlatformJobs();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [registerJob, upsertJob, disconnectStream, ensureLiveUpdates]);

  const value = useMemo<JobTrackerContextValue>(() => ({
    jobs: state.jobs,
    registerJob,
    dismissJob,
    cancelJob
  }), [state.jobs, registerJob, dismissJob, cancelJob]);

  return <JobTrackerContext.Provider value={value}>{children}</JobTrackerContext.Provider>;
}
